type RecordLike = Record<string, unknown>;

type AnimationOption = {
    value: string;
    label: string;
};

export const ANIMATION_TICKS: readonly number[] = [
    0, 0.25, 0.4, 0.5, 0.7, 1, 1.25, 1.5, 2,
];

function toRecord(value: unknown): RecordLike | null {
    return value && typeof value === 'object' ? (value as RecordLike) : null;
}

function valOf(value: unknown): unknown {
    const record = toRecord(value);
    return record && 'value' in record ? record.value : value;
}

function nonEmptyArr(value: unknown): value is unknown[] {
    return Array.isArray(value) && value.length > 0;
}

export function snapDuration(seconds: unknown = 0): number {
    const normalizedSeconds = Math.max(0, Math.min(2, Number(seconds) || 0));
    let best = ANIMATION_TICKS[0];
    let diff = Infinity;

    for (const tick of ANIMATION_TICKS) {
        const currentDiff = Math.abs(tick - normalizedSeconds);
        if (currentDiff < diff) {
            diff = currentDiff;
            best = tick;
        }
    }

    return best;
}

export function normalizeAnimationPreset(
    v: unknown,
    group = 'content'
): string {
    const map: Record<string, string> = {
        '': 'slide',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: group === 'features' ? 'stagger' : 'fade',
        cascade: group === 'features' ? 'stagger' : 'fade',
    };

    const key = String(v ?? '')
        .toLowerCase()
        .trim();

    return map[key] ?? 'slide';
}

export function getSupportedAnimationGroups(props: RecordLike = {}): string[] {
    const features = valOf(props.Features);
    const reviews = valOf(props.Reviews);
    const people = valOf(props.People);
    const packages = valOf(props.Packages);

    const requestedRaw = String(valOf(props.AnimationGroup) ?? '')
        .toLowerCase()
        .trim();
    const requested =
        requestedRaw === 'badges' || requestedRaw === 'badge'
            ? 'features'
            : requestedRaw;

    const supports = new Set<string>(['content']);

    const badges = valOf(props.Badges);
    const logos = valOf(props.Logos);
    if (
        nonEmptyArr(badges) ||
        nonEmptyArr(toRecord(badges)?.value) ||
        nonEmptyArr(logos) ||
        nonEmptyArr(toRecord(logos)?.value)
    ) {
        supports.add('features');
    }

    if (
        nonEmptyArr(features) ||
        nonEmptyArr(reviews) ||
        nonEmptyArr(toRecord(features)?.value) ||
        nonEmptyArr(toRecord(reviews)?.value) ||
        nonEmptyArr(people) ||
        nonEmptyArr(toRecord(people)?.value)
    ) {
        supports.add('features');
    }

    const steps = valOf(props.Steps);
    if (nonEmptyArr(steps) || nonEmptyArr(toRecord(steps)?.value)) {
        supports.add('features');
    }

    if (Array.isArray(packages)) {
        const anyPackageFeatures = packages.some((pkg) => {
            const pkgRecord = toRecord(pkg);
            return nonEmptyArr(valOf(pkgRecord?.Features));
        });
        if (anyPackageFeatures || packages.length > 0) {
            supports.add('features');
        }
    }

    const services = valOf(props.Services);
    const hasServicesKey = Object.prototype.hasOwnProperty.call(
        props,
        'Services'
    );

    if (Array.isArray(services)) {
        const anyServiceFeatures = services.some((service) => {
            const serviceRecord = toRecord(service);
            return nonEmptyArr(valOf(serviceRecord?.Features));
        });

        const anyServiceMedias = services.some((service) => {
            const serviceRecord = toRecord(service);
            return Boolean(
                String(valOf(serviceRecord?.Image) ?? '') ||
                    String(valOf(serviceRecord?.ImageUrl) ?? '') ||
                    nonEmptyArr(valOf(serviceRecord?.Medias)) ||
                    String(valOf(serviceRecord?.BeforeImage) ?? '') ||
                    String(valOf(serviceRecord?.AfterImage) ?? '')
            );
        });

        const anyServiceCtas = services.some((service) => {
            const serviceRecord = toRecord(service);
            return Boolean(
                String(valOf(serviceRecord?.CTA) ?? '') ||
                    String(valOf(serviceRecord?.SecondaryButtonText) ?? '') ||
                    String(valOf(serviceRecord?.OutlineButtonText) ?? '')
            );
        });

        if (services.length > 0 || anyServiceFeatures) supports.add('features');
        if (anyServiceMedias) supports.add('medias');
        if (anyServiceCtas) supports.add('ctas');
    } else if (hasServicesKey) {
        supports.add('features');
    }

    const medias =
        valOf(props.Medias) || valOf(props.Media) || valOf(props.Files);
    const singleImage =
        String(valOf(props.Image) ?? '') || String(valOf(props.ImageUrl) ?? '');

    const hasSlider =
        String(valOf(props.BeforeImage) ?? '') ||
        String(valOf(props.AfterImage) ?? '');

    if (
        nonEmptyArr(medias) ||
        nonEmptyArr(toRecord(medias)?.value) ||
        hasSlider ||
        singleImage
    ) {
        supports.add('medias');
    }

    const hasCTA =
        String(valOf(props.CTA) ?? '') ||
        String(valOf(props.SecondaryButtonText) ?? '') ||
        String(valOf(props.OutlineButtonText) ?? '') ||
        String(valOf(props.ButtonText) ?? '');
    if (hasCTA) supports.add('ctas');

    if (requested === 'ctas') supports.add(requested);

    return Array.from(supports);
}

export function normalizeAnimationGroup(
    value: unknown,
    props: RecordLike = {}
): string {
    const supported = getSupportedAnimationGroups(props);
    const fallback = supported.includes('features')
        ? 'features'
        : supported.includes('medias')
        ? 'medias'
        : 'content';

    const raw = String(value ?? '')
        .toLowerCase()
        .trim();
    const normalized = raw === 'badges' || raw === 'badge' ? 'features' : raw;

    return supported.includes(normalized) ? normalized : fallback;
}

export function getAnimationOptions(group = 'content'): AnimationOption[] {
    const base: AnimationOption[] = [
        { value: 'none', label: 'None' },
        { value: 'fade', label: 'Fade in' },
        { value: 'slide', label: 'Slide up' },
        { value: 'zoom', label: 'Zoom in' },
    ];

    if (group === 'features') {
        base.push({ value: 'stagger', label: 'Cascade' });
    }

    return base;
}
